import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search } from 'lucide-react';
import { selectAdvisorAdminPortalState } from '@/model/selectors';
import { postAdvisorForAdminSearchRequest } from '@/model/advisorAdminPortalModel';

interface AdminSearchBarProps {
  onAdvisorSelected: (advisor: Advisor) => void;
}

export function AdminSearchBar({ onAdvisorSelected }: AdminSearchBarProps) {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState('');
  const { advisorFetchStatus, advisorFetchError, selectedAdvisor } = useSelector(
    selectAdvisorAdminPortalState,
  );

  const handleSearch = () => {
    if (!searchQuery.trim()) return;

    const query = searchQuery.trim();
    const isEmail = query.includes('@');

    dispatch(
      postAdvisorForAdminSearchRequest(isEmail ? { advisorEmail: query } : { advisorId: query }),
    );
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // Call callback when advisor is found
  React.useEffect(() => {
    if (selectedAdvisor) {
      onAdvisorSelected(selectedAdvisor);
    }
  }, [selectedAdvisor]);

  const isLoading = advisorFetchStatus === 'loading';

  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Search by ID or email..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          className="h-7 w-56 rounded border border-orange-300 bg-white pl-8 pr-3 text-xs text-gray-900 placeholder:text-gray-500 focus:border-orange-400 focus:outline-none focus:ring-1 focus:ring-orange-400 disabled:opacity-50"
        />
      </div>
      <button
        onClick={handleSearch}
        disabled={!searchQuery.trim() || isLoading}
        className="h-7 rounded border border-orange-300 bg-white px-3 text-xs font-medium text-orange-700 transition-colors hover:bg-orange-50 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isLoading ? 'Searching...' : 'Search'}
      </button>
      {advisorFetchError && <span className="text-xs text-red-600">{advisorFetchError}</span>}
    </div>
  );
}
